import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { getAnswerValuesFromItems } from '../../../../../helpers'

/**
 * Immunizations subsection adapter
 * Handles vaccine/date pairs for various immunizations (13 fields)
 * This follows the MultiColumn pattern: vaccine + date pairs
 */
export class ImmunizationsAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hepatitis B vaccine/date pair
    const hepB = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B
    )?.at(0)

    const hepBDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE
    )?.at(0)

    // Meningococcal vaccine/date pair
    const meningococcal = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL
    )?.at(0)

    const meningococcalDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE
    )?.at(0)

    // COVID-19 vaccine/date pair
    const covid19 = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19
    )?.at(0)

    const covid19Date = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE
    )?.at(0)

    // DTaP vaccine/date pair
    const dtap = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP
    )?.at(0)

    const dtapDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE
    )?.at(0)

    // HPV vaccine/date pair
    const hpv = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV
    )?.at(0)

    const hpvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE
    )?.at(0)

    // RSV vaccine/date pair
    const rsv = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV
    )?.at(0)

    const rsvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE
    )?.at(0)

    // Notes field
    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES
    )?.at(0)

    return {
      [RecertFormField.IMMUNIZATIONS_HEP_B]: hepB,
      [RecertFormField.IMMUNIZATIONS_HEP_B_DATE]: hepBDate,
      [RecertFormField.IMMUNIZATIONS_MENINGOCOCCAL]: meningococcal,
      [RecertFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE]: meningococcalDate,
      [RecertFormField.IMMUNIZATIONS_COVID_19]: covid19,
      [RecertFormField.IMMUNIZATIONS_COVID_19_DATE]: covid19Date,
      [RecertFormField.IMMUNIZATIONS_DTAP]: dtap,
      [RecertFormField.IMMUNIZATIONS_DTAP_DATE]: dtapDate,
      [RecertFormField.IMMUNIZATIONS_HPV]: hpv,
      [RecertFormField.IMMUNIZATIONS_HPV_DATE]: hpvDate,
      [RecertFormField.IMMUNIZATIONS_RSV]: rsv,
      [RecertFormField.IMMUNIZATIONS_RSV_DATE]: rsvDate,
      [RecertFormField.IMMUNIZATIONS_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_HEP_B)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_HEP_B)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_HEP_B_DATE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_HEP_B_DATE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_MENINGOCOCCAL)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_MENINGOCOCCAL)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_COVID_19)) {
      mutations.push({
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_COVID_19)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_COVID_19_DATE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_COVID_19_DATE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_DTAP)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_DTAP)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_DTAP_DATE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_DTAP_DATE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_HPV)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_HPV)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_HPV_DATE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_HPV_DATE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_RSV)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_RSV)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_RSV_DATE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_RSV_DATE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.IMMUNIZATIONS_NOTES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES,
        value: this.getFormDataField(RecertFormField.IMMUNIZATIONS_NOTES)
      })
    }

    return mutations
  }
}
